import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Grid,
  Autocomplete,
  Chip,
  Fade,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  LinearProgress,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Apple as AppleIcon,
  Public as PublicIcon,
  AutoAwesome as AIIcon,
  Verified as VerifiedIcon,
  Lock as LockIcon,
  Shield as ShieldIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL } from '../../constants';
import { API_BASE_URL as BACKEND_BASE } from '../../config/api';
import { ssoBackendOrigin } from '../../lib/ssoBackend';
import TurnstileBox, { isTurnstileEnabled } from '../../components/Auth/TurnstileBox';

// Same rotating backgrounds as LoginPage for visual continuity
const BACKGROUNDS = [
  { url: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=95', location: 'Paris, France' },
  { url: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&q=95', location: 'London, United Kingdom' },
  { url: 'https://images.unsplash.com/photo-1538332576228-eb5b4c4de6f5?w=1920&q=95', location: 'Dubai, UAE' },
  { url: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=1920&q=95', location: 'New York, USA' },
  { url: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=1920&q=95', location: 'Sydney, Australia' },
  { url: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1920&q=95', location: 'Tokyo, Japan' },
  { url: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1920&q=95', location: 'Rome, Italy' },
  { url: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1920&q=95', location: 'Singapore' },
];

const ACCOUNT_TYPES = [
  { value: 'general', label: 'Individual', description: 'Personal visa applications' },
  { value: 'agent', label: 'Travel Agent', description: 'Manage client applications' },
  { value: 'corporate', label: 'Corporate', description: 'Business travel management' },
  { value: 'enterprise', label: 'Enterprise', description: 'Large-scale workforce visa management' },
];

interface Country {
  id: number;
  name: string;
  iso2: string;
  iso3: string;
}

// Dark themed text field styling — matches LoginPage
const darkFieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.06)',
    borderRadius: 2,
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&.Mui-focused fieldset': { borderColor: '#0EA5E9' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0EA5E9' },
  '& .MuiInputBase-input': { color: 'white' },
  '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' },
  '& .MuiFormHelperText-root': { color: '#FCA5A5' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.5)' },
};

const RegisterPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { register, isLoading, isAuthenticated, user } = useAuth();
  const { showError, showSuccess } = useNotification();

  // Redirect already-authenticated users (verify gate first)
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(user.isEmailVerified ? '/dashboard' : '/verify-email', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const [currentBg, setCurrentBg] = useState(() => Math.floor(Math.random() * BACKGROUNDS.length));
  const [nextBg, setNextBg] = useState<number | null>(null);
  const [fading, setFading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1); // 2-step form for better UX
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');

  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    nationality: '',
    dateOfBirth: '',
    accountType: 'general',
    agreeToTerms: false,
    subscribeNewsletter: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // SSO redirect — requires Terms acceptance first
  const handleSSORegister = (provider: 'google' | 'apple') => {
    if (!formData.agreeToTerms) {
      setErrors((prev) => ({
        ...prev,
        agreeToTerms: 'Accept the Terms of Use and Privacy Policy before continuing with Google or Apple.',
      }));
      setRegisterError('Please accept the Terms of Use and Privacy Policy to continue.');
      return;
    }
    const backendBase = ssoBackendOrigin(BACKEND_BASE);
    window.location.href = `${backendBase}/api/v1/auth/sso/${provider}`;
  };

  /** Platform Hub SSO — requires Terms acceptance first */
  const handleHubSSORegister = () => {
    if (!formData.agreeToTerms) {
      setErrors((prev) => ({
        ...prev,
        agreeToTerms: 'Accept the Terms of Use and Privacy Policy before continuing with Tripnovac Hub.',
      }));
      setRegisterError('Please accept the Terms of Use and Privacy Policy to continue.');
      return;
    }
    const backendBase = ssoBackendOrigin(BACKEND_BASE);
    window.location.href = `${backendBase}/api/v1/sso/login/platform-hub`;
  };

  // Preload backgrounds
  useEffect(() => {
    BACKGROUNDS.forEach(bg => { const img = new Image(); img.src = bg.url; });
  }, []);

  // Rotate backgrounds every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setNextBg((currentBg + 1) % BACKGROUNDS.length);
      setFading(true);
      setTimeout(() => {
        setCurrentBg(prev => (prev + 1) % BACKGROUNDS.length);
        setFading(false);
        setNextBg(null);
      }, 1000);
    }, 8000);
    return () => clearInterval(interval);
  }, [currentBg]);

  // Fetch countries from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/countries?limit=300`);
        const data = await response.json();
        const list = data.data || data.countries || (Array.isArray(data) ? data : []);
        if (list.length) {
          const sorted = list.sort((a: Country, b: Country) =>
            a.name.localeCompare(b.name)
          );
          setCountries(sorted);
        }
      } catch (err) {
        console.error('Failed to fetch countries:', err);
        setCountries([
          { id: 1, name: 'United States', iso2: 'US', iso3: 'USA' },
          { id: 2, name: 'United Kingdom', iso2: 'GB', iso3: 'GBR' },
          { id: 3, name: 'Canada', iso2: 'CA', iso3: 'CAN' },
          { id: 4, name: 'Australia', iso2: 'AU', iso3: 'AUS' },
          { id: 5, name: 'Germany', iso2: 'DE', iso3: 'DEU' },
          { id: 6, name: 'France', iso2: 'FR', iso3: 'FRA' },
          { id: 7, name: 'India', iso2: 'IN', iso3: 'IND' },
          { id: 8, name: 'China', iso2: 'CN', iso3: 'CHN' },
          { id: 9, name: 'Japan', iso2: 'JP', iso3: 'JPN' },
          { id: 10, name: 'Brazil', iso2: 'BR', iso3: 'BRA' },
        ]);
      } finally {
        setCountriesLoading(false);
      }
    };
    fetchCountries();
  }, []);

  const bg = BACKGROUNDS[currentBg];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    setRegisterError(null);
  };

  // Password strength indicator
  const getPasswordStrength = useCallback((password: string) => {
    if (!password) return { score: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 1) return { score: 20, label: 'Weak', color: '#EF4444' };
    if (score === 2) return { score: 40, label: 'Fair', color: '#F59E0B' };
    if (score === 3) return { score: 60, label: 'Good', color: '#F59E0B' };
    if (score === 4) return { score: 80, label: 'Strong', color: '#10B981' };
    return { score: 100, label: 'Very Strong', color: '#10B981' };
  }, []);

  const validateStep1 = useCallback(() => {
    const e: Record<string, string> = {};
    if (!formData.firstName.trim()) e.firstName = 'First name is required';
    if (!formData.lastName.trim()) e.lastName = 'Last name is required';
    if (!formData.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Please enter a valid email';
    if (!formData.phone.trim()) e.phone = 'Phone number is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [formData]);

  const validateStep2 = useCallback(() => {
    const e: Record<string, string> = {};
    if (!formData.password) e.password = 'Password is required';
    else if (formData.password.length < 8) e.password = 'Password must be at least 8 characters';
    if (!formData.confirmPassword) e.confirmPassword = 'Please confirm your password';
    else if (formData.password !== formData.confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (!formData.nationality.trim()) e.nationality = 'Nationality is required';
    if (!formData.agreeToTerms) e.agreeToTerms = 'You must agree to continue';
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [formData]);

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
      setErrors({});
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) return;
    if (isTurnstileEnabled() && !turnstileToken) {
      setRegisterError('Complete the security check and try again.');
      showError('Complete the security check and try again.');
      return;
    }

    setRegisterError(null);
    try {
      const userData = {
        fullName: `${formData.firstName} ${formData.lastName}`,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        nationality: formData.nationality,
        dateOfBirth: formData.dateOfBirth || undefined,
        role: formData.accountType,
        subscribeNewsletter: formData.subscribeNewsletter,
        agreeToTerms: formData.agreeToTerms,
        turnstileToken,
      };

      const result = await register(userData);

      if (result.success) {
        showSuccess(result.message || 'Account created. Check your email for a verification code.');
        setTimeout(() => navigate('/verify-email', { replace: true }), 400);
      } else {
        setRegisterError(result.message || 'Registration failed');
        showError(result.message || 'Registration failed');
      }
    } catch {
      setRegisterError('An unexpected error occurred. Please try again.');
      showError('An unexpected error occurred. Please try again.');
    }
  };

  const pwStrength = getPasswordStrength(formData.password);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', position: 'relative', overflow: 'hidden', bgcolor: '#0F172A' }}>
      {/* Rotating Background */}
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${bg.url})`, backgroundSize: 'cover', backgroundPosition: 'center',
          transition: 'opacity 1s ease-in-out', opacity: fading ? 0 : 1,
          '&::after': { content: '""', position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(15,23,42,0.75) 0%, rgba(15,23,42,0.45) 50%, rgba(15,23,42,0.65) 100%)' },
        }} />
        {nextBg !== null && (
          <Box sx={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${BACKGROUNDS[nextBg].url})`, backgroundSize: 'cover', backgroundPosition: 'center',
            transition: 'opacity 1s ease-in-out', opacity: fading ? 1 : 0,
            '&::after': { content: '""', position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(15,23,42,0.75) 0%, rgba(15,23,42,0.45) 50%, rgba(15,23,42,0.65) 100%)' },
          }} />
        )}
      </Box>

      {/* Top Bar — Feature Chips */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5 }}>
          {[
            { icon: <PublicIcon sx={{ fontSize: 16 }} />, text: '249 countries' },
            { icon: <AIIcon sx={{ fontSize: 16 }} />, text: 'AI-Powered' },
            { icon: <VerifiedIcon sx={{ fontSize: 16 }} />, text: 'Premium' },
          ].map((item, i) => (
            <Chip key={i} icon={item.icon} label={item.text} size="small"
              sx={{ bgcolor: alpha('#FFFFFF', 0.15), backdropFilter: 'blur(10px)', color: 'white', fontWeight: 500, fontSize: '0.75rem', '& .MuiChip-icon': { color: 'white' }, border: `1px solid ${alpha('#FFFFFF', 0.2)}` }}
            />
          ))}
        </Box>
        <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 4, color: 'white' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700}>Online</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Support Requests</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700}>100%</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Secure</Typography>
          </Box>
        </Box>
      </Box>

      {/* Registration Card */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, position: 'relative', p: { xs: 2, md: 4 } }}>
        <Fade in timeout={500}>
          <Box sx={{
            width: '100%', maxWidth: 480,
            background: 'linear-gradient(145deg, #1a1f3c 0%, #0d1025 50%, #141832 100%)',
            backdropFilter: 'blur(20px)', borderRadius: 3, p: { xs: 2.5, sm: 3 },
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            maxHeight: '90vh', overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2 },
          }}>
            {/* Logo */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box component="img" src="/assets/app-icon.png" alt="Tripnovac Visa"
                sx={{ height: 56, width: 'auto', mb: 0.5 }}
                onError={(e: any) => { e.target.style.display = 'none'; }}
              />
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                Create Your Account
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                Join Tripnovac — Enterprise visa processing for 249 countries and territories (ISO 3166-1)
              </Typography>
            </Box>

            {/* Step Indicator */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Box sx={{ flex: 1, height: 3, borderRadius: 2, bgcolor: '#0EA5E9' }} />
              <Box sx={{ flex: 1, height: 3, borderRadius: 2, bgcolor: step >= 2 ? '#0EA5E9' : 'rgba(255,255,255,0.15)', transition: 'background-color 0.3s' }} />
            </Box>

            {/* Error Banner */}
            {registerError && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: alpha('#EF4444', 0.15), border: `1px solid ${alpha('#EF4444', 0.3)}` }}>
                <Typography variant="caption" sx={{ color: '#FCA5A5' }}>{registerError}</Typography>
              </Box>
            )}

            {/* Step 1: Personal Info */}
            {step === 1 && (
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 1.5, display: 'block' }}>
                  Step 1 — Personal Information
                </Typography>

                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField fullWidth placeholder="First Name" name="firstName" size="small"
                      value={formData.firstName} onChange={handleChange}
                      error={!!errors.firstName} helperText={errors.firstName}
                      autoComplete="given-name" autoFocus sx={darkFieldSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth placeholder="Last Name" name="lastName" size="small"
                      value={formData.lastName} onChange={handleChange}
                      error={!!errors.lastName} helperText={errors.lastName}
                      autoComplete="family-name" sx={darkFieldSx}
                    />
                  </Grid>
                </Grid>

                <TextField fullWidth placeholder="Email Address" name="email" type="email" size="small"
                  value={formData.email} onChange={handleChange}
                  error={!!errors.email} helperText={errors.email}
                  autoComplete="email" sx={{ ...darkFieldSx, mt: 1.5 }}
                />

                <TextField fullWidth placeholder="Phone Number" name="phone" size="small"
                  value={formData.phone} onChange={handleChange}
                  error={!!errors.phone} helperText={errors.phone}
                  autoComplete="tel" sx={{ ...darkFieldSx, mt: 1.5 }}
                />

                {/* Account Type */}
                <FormControl fullWidth size="small" sx={{ mt: 1.5, ...darkFieldSx, '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' } }}>
                  <InputLabel sx={{ color: 'rgba(255,255,255,0.5)' }}>Account Type</InputLabel>
                  <Select
                    value={formData.accountType}
                    label="Account Type"
                    onChange={(e) => setFormData(prev => ({ ...prev, accountType: e.target.value }))}
                    sx={{ color: 'white' }}
                  >
                    {ACCOUNT_TYPES.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{type.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{type.description}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button fullWidth variant="contained" onClick={handleNext}
                  sx={{
                    mt: 2, py: 1.2, borderRadius: 2, fontWeight: 600, fontSize: '0.9rem', textTransform: 'none',
                    background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                    boxShadow: '0 4px 20px rgba(14, 165, 233, 0.3)',
                    '&:hover': { background: 'linear-gradient(135deg, #0284C7 0%, #0D9488 100%)', boxShadow: '0 10px 40px rgba(14, 165, 233, 0.4)' },
                  }}>
                  Continue
                </Button>

                {/* SSO Options — Terms must be accepted first */}
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', mb: 0.5, mt: 0.5 }}
                  control={
                    <Checkbox
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleChange}
                      size="small"
                      sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#0EA5E9' }, pt: 0.3 }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>
                      I agree to the{' '}
                      <Link component={RouterLink} to="/terms" target="_blank" sx={{ color: '#38bdf8' }}>
                        Terms of Use
                      </Link>{' '}
                      and{' '}
                      <Link component={RouterLink} to="/privacy" target="_blank" sx={{ color: '#38bdf8' }}>
                        Privacy Policy
                      </Link>
                      . I understand Tripnovac Visa provides software tools and information subject to plan
                      limits and does not guarantee visa approval or government outcomes.
                    </Typography>
                  }
                />
                {errors.agreeToTerms && (
                  <Typography variant="caption" sx={{ color: '#FCA5A5', display: 'block', ml: 4, mb: 0.5 }}>
                    {errors.agreeToTerms}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', my: 1.5 }}>
                  <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
                  <Typography variant="caption" sx={{ px: 1.5, color: 'rgba(255, 255, 255, 0.6)' }}>or sign up with</Typography>
                  <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PublicIcon sx={{ fontSize: 18 }} />}
                  onClick={handleHubSSORegister}
                  sx={{
                    mb: 1,
                    py: 1,
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)',
                    border: '1px solid rgba(14, 165, 233, 0.45)',
                    color: '#E0F2FE',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0B1220 0%, #164E63 100%)',
                      borderColor: '#38bdf8',
                    },
                  }}
                >
                  Continue with Tripnovac Hub
                </Button>

                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  <Button fullWidth variant="outlined" startIcon={<GoogleIcon sx={{ fontSize: 18 }} />}
                    onClick={() => handleSSORegister('google')}
                    sx={{ py: 0.9, borderRadius: 2, borderColor: '#0EA5E9', color: '#0EA5E9', fontWeight: 500, fontSize: '0.8rem', bgcolor: alpha('#0EA5E9', 0.1), '&:hover': { borderColor: '#38bdf8', bgcolor: alpha('#0EA5E9', 0.2), color: '#38bdf8' } }}>
                    Google
                  </Button>
                  <Button fullWidth variant="outlined" startIcon={<AppleIcon sx={{ fontSize: 18 }} />}
                    onClick={() => handleSSORegister('apple')}
                    sx={{ py: 0.9, borderRadius: 2, borderColor: '#0EA5E9', color: '#0EA5E9', fontWeight: 500, fontSize: '0.8rem', bgcolor: alpha('#0EA5E9', 0.1), '&:hover': { borderColor: '#38bdf8', bgcolor: alpha('#0EA5E9', 0.2), color: '#38bdf8' } }}>
                    Apple
                  </Button>
                </Box>
                {/* Honeypot — leave empty; bots often fill it */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                  onChange={() => {}}
                />
              </Box>
            )}

            {/* Step 2: Security & Details */}
            {step === 2 && (
              <Box component="form" onSubmit={handleSubmit}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 1.5, display: 'block' }}>
                  Step 2 — Security & Details
                </Typography>

                <Autocomplete
                  options={countries}
                  getOptionLabel={(option) => option.name}
                  loading={countriesLoading}
                  value={countries.find(c => c.name === formData.nationality) || null}
                  onChange={(_, newValue) => {
                    setFormData(prev => ({ ...prev, nationality: newValue?.name || '' }));
                    if (errors.nationality) setErrors(prev => ({ ...prev, nationality: '' }));
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img
                          src={`https://flagcdn.com/16x12/${option.iso2.toLowerCase()}.png`}
                          alt=""
                          style={{ width: 16, height: 12, borderRadius: 1 }}
                          onError={(e: any) => { e.target.style.display = 'none'; }}
                        />
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Nationality"
                      size="small"
                      error={!!errors.nationality}
                      helperText={errors.nationality}
                      sx={darkFieldSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {countriesLoading ? <CircularProgress sx={{ color: 'rgba(255,255,255,0.5)' }} size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mb: 1.5 }}
                />

                <TextField fullWidth placeholder="Date of Birth" name="dateOfBirth" type="date" size="small"
                  value={formData.dateOfBirth} onChange={handleChange}
                  error={!!errors.dateOfBirth} helperText={errors.dateOfBirth}
                  InputLabelProps={{ shrink: true }}
                  sx={{ ...darkFieldSx, mb: 1.5, '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' } }}
                />

                <TextField fullWidth placeholder="Password" name="password" size="small"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password} onChange={handleChange}
                  error={!!errors.password} helperText={errors.password}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <Box
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{ cursor: 'pointer', display: 'flex', color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}
                      >
                        {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                      </Box>
                    ),
                  }}
                  sx={{ ...darkFieldSx, mb: 0.5 }}
                />

                {/* Password Strength */}
                {formData.password && (
                  <Box sx={{ mb: 1.5 }}>
                    <LinearProgress variant="determinate" value={pwStrength.score}
                      sx={{ height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: pwStrength.color, borderRadius: 2 } }}
                    />
                    <Typography variant="caption" sx={{ color: pwStrength.color, fontSize: '0.7rem', mt: 0.3, display: 'block' }}>
                      {pwStrength.label}
                    </Typography>
                  </Box>
                )}

                <TextField fullWidth placeholder="Confirm Password" name="confirmPassword" size="small"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword} onChange={handleChange}
                  error={!!errors.confirmPassword} helperText={errors.confirmPassword}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <Box
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        sx={{ cursor: 'pointer', display: 'flex', color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}
                      >
                        {showConfirmPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                      </Box>
                    ),
                  }}
                  sx={{ ...darkFieldSx, mb: 1.5 }}
                />

                {/* Terms & Newsletter */}
                <FormControlLabel
                  control={<Checkbox name="agreeToTerms" checked={formData.agreeToTerms} onChange={handleChange} size="small"
                    sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#0EA5E9' } }} />}
                  label={
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      I agree to the{' '}
                      <Link component={RouterLink} to="/terms" sx={{ color: '#0EA5E9', textDecoration: 'none' }}>Terms</Link>
                      {' '}and{' '}
                      <Link component={RouterLink} to="/privacy" sx={{ color: '#0EA5E9', textDecoration: 'none' }}>Privacy Policy</Link>
                      . This product does not guarantee visa approval or government outcomes.
                    </Typography>
                  }
                />
                {errors.agreeToTerms && (
                  <Typography variant="caption" sx={{ color: '#FCA5A5', display: 'block', ml: 4, mt: -0.5, mb: 0.5 }}>{errors.agreeToTerms}</Typography>
                )}

                <FormControlLabel
                  control={<Checkbox name="subscribeNewsletter" checked={formData.subscribeNewsletter} onChange={handleChange} size="small"
                    sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#0EA5E9' } }} />}
                  label={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Subscribe for visa updates & travel tips</Typography>}
                  sx={{ mb: 1 }}
                />

                <TurnstileBox onToken={setTurnstileToken} />

                {/* Buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" onClick={() => { setStep(1); setErrors({}); }}
                    sx={{ py: 1.2, borderRadius: 2, fontWeight: 500, fontSize: '0.85rem', textTransform: 'none', borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.05)' } }}>
                    Back
                  </Button>
                  <Button type="submit" fullWidth variant="contained" disabled={isLoading}
                    sx={{
                      py: 1.2, borderRadius: 2, fontWeight: 600, fontSize: '0.9rem', textTransform: 'none',
                      background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                      boxShadow: '0 4px 20px rgba(14, 165, 233, 0.3)',
                      '&:hover': { background: 'linear-gradient(135deg, #0284C7 0%, #0D9488 100%)', boxShadow: '0 10px 40px rgba(14, 165, 233, 0.4)' },
                      '&:disabled': { background: '#CBD5E1' },
                    }}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Sign In Link */}
            <Typography variant="caption" textAlign="center" display="block" sx={{ mt: 1.5, color: 'rgba(255, 255, 255, 0.8)' }}>
              Already have an account?{' '}
              <Link component={RouterLink} to="/login" sx={{ color: '#0EA5E9', fontWeight: 600, textDecoration: 'none' }}>
                Sign in
              </Link>
            </Typography>

            {/* Security Badges */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, pt: 1.5, mt: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.15)' }}>
              {[
                { icon: <LockIcon sx={{ fontSize: 14 }} />, text: 'HTTPS' },
                { icon: <ShieldIcon sx={{ fontSize: 14 }} />, text: 'Role Access' },
                { icon: <VerifiedIcon sx={{ fontSize: 14 }} />, text: 'Stripe Payments' },
              ].map((badge, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ color: '#10B981' }}>{badge.icon}</Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} fontWeight={500}>{badge.text}</Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1, fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.5)' }}>
              &copy; 2026 Tripnovac by Alpha Libra LLC
            </Typography>
          </Box>
        </Fade>
      </Box>

      {/* Bottom Left — Benefits */}
      {!isMobile && (
        <Box sx={{ position: 'absolute', bottom: 20, left: 20, zIndex: 10, maxWidth: 420 }}>
          <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.7), fontWeight: 500, mb: 1, display: 'block' }}>
            Why Tripnovac?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {[
              { text: 'Application Preparation', color: '#10B981' },
              { text: 'Document Checklists', color: '#0EA5E9' },
              { text: 'Support Requests', color: '#F59E0B' },
              { text: '249 destinations', color: '#8B5CF6' },
            ].map((item, i) => (
              <Chip key={i} label={item.text} size="small"
                sx={{ bgcolor: alpha(item.color, 0.2), color: 'white', fontWeight: 500, border: `1px solid ${alpha(item.color, 0.4)}`, backdropFilter: 'blur(10px)' }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Bottom Right — Tagline */}
      <Box sx={{ position: 'absolute', bottom: 20, right: 20, zIndex: 10, textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
          Start Your Journey
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: 350, mt: 0.5 }}>
          Enterprise Visa Processing Platform
        </Typography>
      </Box>
    </Box>
  );
};

export default RegisterPage;
